import { useCallback, useEffect, useState } from 'react';
import { ScanLine } from 'lucide-react';
import Button from '../ui/Button';
import BarcodeScanner from '../shared/BarcodeScanner';
import FormModal, { Field, inputClass } from '../fieldWork/FormModal';
import harvestService from '../../services/harvest';
import { errorMessage } from '../../utils/apiResponse';
import { workerDisplayName } from '../../types/fieldWork';
import { binPlace, type Bin } from '../../types/harvest';
import { DateRangeInputs, Notice } from '../labour/shared';
import { useDateRange } from '../labour/labourUtils';

const STATUS_LABEL: Record<string, string> = {
  started: 'In the row', full: 'Full, not tagged', tagged: 'Tagged', loaded: 'On a load',
  delivered: 'Delivered', received: 'Received', rejected: 'Rejected', void: 'Void',
};

/**
 * Every bin in a range: find one by its tag (typed or scanned), correct its
 * fill or chalk code with a reason, or void it. Voided bins leave every total.
 */
export default function BinsPanel() {
  const range = useDateRange();
  const [bins, setBins] = useState<Bin[]>([]);
  const [reviewOnly, setReviewOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tag, setTag] = useState('');
  const [scanning, setScanning] = useState(false);
  const [editing, setEditing] = useState<Bin | null>(null);

  const load = useCallback(async () => {
    try {
      setBins(await harvestService.listBins({ from: range.from, to: range.to, needs_review: reviewOnly || undefined }));
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load bins'));
    }
  }, [range.from, range.to, reviewOnly]);
  useEffect(() => { load(); }, [load]);

  const findTag = async (code: string) => {
    setScanning(false);
    try {
      const b = await harvestService.findByTag(code);
      if (b) setEditing(b);
      else setError(`No bin has the tag ${code}.`);
    } catch (e) {
      setError(errorMessage(e, 'That is not a valid tag'));
    }
  };

  const tagged = bins.filter(b => b.tag_code).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <DateRangeInputs {...range} />
        <form className="flex items-end gap-2" onSubmit={e => { e.preventDefault(); if (tag.trim()) findTag(tag); }}>
          <label className="text-xs text-neutral-600">
            <span className="block mb-1">Find a tag</span>
            <input className={`${inputClass} w-32 font-mono`} value={tag} placeholder="4817-6" onChange={e => setTag(e.target.value)} />
          </label>
          <Button type="button" size="sm" variant="outline" onClick={() => setScanning(true)} aria-label="Scan a tag"><ScanLine className="h-4 w-4" /></Button>
        </form>
        <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
          <input type="checkbox" checked={reviewOnly} onChange={e => setReviewOnly(e.target.checked)}
            className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200" />
          <span className="text-neutral-600">Only bins needing a block</span>
        </label>
      </div>
      {error && <Notice tone="error">{error}</Notice>}
      <p className="text-sm text-neutral-500">{bins.length} bins · {tagged} tagged</p>
      {bins.length > 0 && (
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg" aria-label="Bins">
          {bins.map(b => (
            <li key={b.id}>
              <button type="button" onClick={() => setEditing(b)} className="w-full text-left px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-mono font-semibold text-neutral-900 w-28">{b.tag_code ?? b.field_code ?? '—'}</span>
                <span className="flex-1 min-w-[10rem] text-sm text-neutral-700">
                  {binPlace(b)}{b.fill_fraction < 1 ? ` · ${Math.round(b.fill_fraction * 100)}%` : ''}
                </span>
                <span className="text-xs text-neutral-500">{b.local_date}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${b.needs_review ? 'bg-amber-50 text-amber-700' : 'bg-neutral-100 text-neutral-600'}`}>
                  {b.needs_review ? 'Block to confirm' : STATUS_LABEL[b.status] ?? b.status}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {scanning && <BarcodeScanner label="Scan a bin tag" onScan={findTag} onClose={() => setScanning(false)} />}
      {editing && <EditBinModal bin={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function EditBinModal({ bin, onClose, onSaved }: { bin: Bin; onClose: () => void; onSaved: () => void }) {
  const [code, setCode] = useState(bin.field_code ?? '');
  const [fill, setFill] = useState(bin.fill_fraction);
  const [row, setRow] = useState(bin.row_number ? String(bin.row_number) : '');
  const [reason, setReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    try {
      if (voiding) await harvestService.voidBin(bin.id, reason);
      else await harvestService.updateBin(bin.id, { field_code: code || null, fill_fraction: fill, row_number: row ? Number(row) : null, reason });
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal title={bin.tag_code ? `Bin ${bin.tag_code}` : `Bin ${bin.field_code ?? ''}`} submitLabel={voiding ? 'Void' : 'Save'}
      onClose={onClose} onSubmit={submit} submitting={saving} submitDisabled={reason.trim().length < 3 || !!bin.voided_at} error={error}>
      <p className="text-sm text-neutral-700">
        {binPlace(bin)}{bin.cultivar_name ? ` · ${bin.cultivar_name}` : ''} · picked {bin.local_date}
        {bin.pickers.length ? ` by ${bin.pickers.map(p => workerDisplayName(p)).join(', ')}` : ''}
      </p>
      {bin.voided_at && <Notice tone="warn">Voided: {bin.void_reason}</Notice>}
      {!voiding && !bin.voided_at && (
        <div className="grid grid-cols-3 gap-3">
          <Field label="Chalk code" htmlFor="eb-code">
            <input id="eb-code" className={`${inputClass} uppercase`} value={code} onChange={e => setCode(e.target.value.toUpperCase())} />
          </Field>
          <Field label="Row" htmlFor="eb-row">
            <input id="eb-row" inputMode="numeric" className={inputClass} value={row} onChange={e => setRow(e.target.value.replace(/\D/g, ''))} />
          </Field>
          <Field label="Full" htmlFor="eb-fill">
            <select id="eb-fill" className={inputClass} value={fill} onChange={e => setFill(Number(e.target.value))}>
              {[1, 0.75, 0.5, 0.25].map(v => <option key={v} value={v}>{v === 1 ? 'Full' : `${v * 100}%`}</option>)}
            </select>
          </Field>
        </div>
      )}
      {!bin.voided_at && (
        <>
          <Field label={voiding ? 'Why is it being voided?' : 'Reason for the change'} htmlFor="eb-reason">
            <input id="eb-reason" className={inputClass} value={reason} onChange={e => setReason(e.target.value)} />
          </Field>
          <button type="button" className="text-sm text-red-600 hover:underline" onClick={() => setVoiding(v => !v)}>
            {voiding ? 'Back to editing' : 'Void this bin instead'}
          </button>
        </>
      )}
    </FormModal>
  );
}
